export type HorizonApiResult = {
  available: boolean;
  message: string;
  payload: unknown;
  status: number;
};

type HorizonPayload = {
  error?: { code?: string; message?: string };
};

export type HorizonWalletChallenge = {
  type: 'horizon.wallet-auth.challenge';
  version: 1;
  challenge_id: string;
  wallet_address: string;
  origin: string;
  issued_at: string;
  expires_at: string;
};

export type HorizonWalletProof = {
  signatureAlgorithm: 'FN-DSA-1024';
  signatureHex: string;
  publicKeyHex: string;
  account: string;
  origin: string;
  method: 'synergy_signMessage';
  payload: HorizonWalletChallenge;
};

type ApiRecord = Record<string, unknown>;

function record(value: unknown): ApiRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : null;
}

function apiBase(): string {
  if (import.meta.env.DEV) return "/horizon-api/v1";

  return window.location.hostname === "horizon.synergysandbox.xyz"
    ? "https://api.horizon.synergysandbox.xyz/v1"
    : "https://api.horizon.synergy-network.io/v1";
}

function unavailableMessage(status: number, code?: string) {
  if (code === "HORIZON_API_NOT_ENABLED") return "The Horizon API route is reachable, but this live service has not been enabled.";
  if (status === 401 || status === 403) return "The live service requires an authenticated Horizon session.";
  return "The live Horizon service did not return an available resource.";
}

export async function fetchHorizonPath(path: string, sessionToken?: string): Promise<HorizonApiResult> {
  if (!path.startsWith("/") || path.includes("..")) {
    return { available: false, message: "The requested Horizon API path is invalid.", payload: null, status: 0 };
  }

  try {
    const response = await fetch(`${apiBase()}${path}`, {
      headers: { Accept: "application/json", ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}) },
    });
    const payload = await response.json().catch(() => null) as HorizonPayload | null;
    const code = payload?.error?.code;
    return {
      available: response.ok,
      message: response.ok ? "Live Horizon service returned a response." : payload?.error?.message || unavailableMessage(response.status, code),
      payload,
      status: response.status,
    };
  } catch {
    return { available: false, message: "The Horizon API could not be reached from this network.", payload: null, status: 0 };
  }
}

export function fetchHorizonResource(resource: "health" | "models" | "agents" | "workflows", sessionToken?: string): Promise<HorizonApiResult> {
  return fetchHorizonPath(`/${resource}`, sessionToken);
}

async function postHorizon<T>(path: string, body: unknown, sessionToken?: string): Promise<T> {
  const response = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null) as HorizonPayload | null;
  if (!response.ok) {
    throw new Error(payload?.error?.message || unavailableMessage(response.status, payload?.error?.code));
  }
  return payload as T;
}

export async function postHorizonPath(path: string, body: unknown, sessionToken: string): Promise<unknown> {
  if (!path.startsWith("/") || path.includes("..") || !sessionToken) throw new Error("The Horizon request is invalid.");
  return postHorizon(path, body, sessionToken);
}

function challengeFrom(value: unknown): HorizonWalletChallenge {
  const challenge = record(record(value)?.challenge);
  if (!challenge || challenge.type !== 'horizon.wallet-auth.challenge' || challenge.version !== 1
    || typeof challenge.challenge_id !== 'string' || typeof challenge.wallet_address !== 'string'
    || typeof challenge.origin !== 'string' || typeof challenge.issued_at !== 'string' || typeof challenge.expires_at !== 'string') {
    throw new Error('Horizon returned an invalid wallet challenge.');
  }
  return challenge as HorizonWalletChallenge;
}

export async function createHorizonWalletChallenge(walletAddress: string): Promise<HorizonWalletChallenge> {
  return challengeFrom(await postHorizon('/auth/challenge', { wallet_address: walletAddress }));
}

export async function verifyHorizonWalletChallenge(challenge: HorizonWalletChallenge, proof: HorizonWalletProof): Promise<{ sessionToken: string; expiresAt: string }> {
  const response = record(await postHorizon('/auth/verify', { challenge_id: challenge.challenge_id, proof }));
  const sessionToken = typeof response?.session_token === 'string' ? response.session_token : '';
  const expiresAt = typeof response?.expires_at === 'string' ? response.expires_at : '';
  if (!sessionToken || !expiresAt) throw new Error('Horizon did not issue a valid wallet session.');
  return { sessionToken, expiresAt };
}

export type HorizonChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type HorizonChatStreamResult = {
  requestId: string | null;
};

function streamEventData(event: string): string | null {
  const lines = event.replaceAll("\r", "").split("\n").filter((line) => line.startsWith("data:"));
  return lines.length ? lines.map((line) => line.slice("data:".length).trimStart()).join("\n") : null;
}

export async function requestHorizonChatStream(
  sessionToken: string,
  messages: HorizonChatMessage[],
  onDelta: (content: string) => void,
  signal?: AbortSignal,
): Promise<HorizonChatStreamResult> {
  if (!sessionToken || messages.length < 1 || messages.length > 32 || messages.some((message) => !message.content.trim())) {
    throw new Error("The Horizon chat request is invalid.");
  }
  const response = await fetch(`${apiBase()}/inference/chat/completions`, {
    method: "POST",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
    body: JSON.stringify({ model: "qwen3-1.7b", messages, max_tokens: 512, temperature: 0.7, stream: true }),
    signal,
  });
  if (!response.ok || !response.body) {
    const payload = await response.json().catch(() => null) as HorizonPayload | null;
    throw new Error(payload?.error?.message || unavailableMessage(response.status, payload?.error?.code));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let pending = "";
  let completed = false;
  const processEvent = (event: string) => {
    const data = streamEventData(event);
    if (!data) return;
    if (data === "[DONE]") {
      completed = true;
      return;
    }
    try {
      const payload = record(JSON.parse(data));
      const choices = Array.isArray(payload?.choices) ? payload.choices : [];
      const choice = record(choices[0]);
      const delta = record(choice?.delta);
      if (typeof delta?.content === "string" && delta.content) onDelta(delta.content);
    } catch {
      // The edge exposes only standard JSON SSE data. An unparseable optional
      // event cannot become a fabricated assistant message.
    }
  };
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });
      let boundary = pending.indexOf("\n\n");
      while (boundary !== -1) {
        processEvent(pending.slice(0, boundary));
        pending = pending.slice(boundary + 2);
        boundary = pending.indexOf("\n\n");
      }
    }
    pending += decoder.decode();
    if (pending) processEvent(pending);
  } finally {
    reader.releaseLock();
  }
  if (!completed) throw new Error("The Horizon response stream ended before completion.");
  return { requestId: response.headers.get("x-request-id") };
}

export async function requestHorizonChatCompletion(sessionToken: string, messages: HorizonChatMessage[]): Promise<string> {
  if (messages.length < 1 || messages.length > 32 || messages.some((message) => !message.content.trim() || message.content.length > 12_000)) {
    throw new Error('The Horizon chat request is invalid.');
  }
  const response = record(await postHorizon('/inference/chat/completions', {
    model: 'qwen3-1.7b',
    messages,
    max_tokens: 512,
    temperature: 0.7,
    stream: false,
  }, sessionToken));
  const choices = Array.isArray(response?.choices) ? response.choices : [];
  const firstChoice = record(choices[0]);
  const message = record(firstChoice?.message);
  const content = typeof message?.content === 'string' ? message.content.trim() : '';
  if (!content) throw new Error('Horizon inference completed without an assistant response.');
  return content;
}
